import { useEffect, useRef, useState } from "react";
import { Button } from "@omnichannel/ui";
import { ApiError, request } from "./api";

interface Case {
  payment: { id: string; provider: string; provider_id: string | null; status: string; amount_minor: string; currency: string; receipt_recorded: boolean };
  hold: { id: string; status: string; created_at: string };
  credit_status: "recorded" | "not_recorded" | "inconsistent";
  credit: { id: string; kind: string; ref: string; amount_minor: string; balance_after_minor: string; occurred_at: string } | null;
  current_balance_minor: string | null;
  observations: { id: string; source_kind: string; source_id: string; amount_minor: string; cumulative_refunded_minor: string; currency: string; occurred_at: string }[];
  observations_truncated: boolean;
}

function exactMoney(minor: string, currency: string) {
  const value = BigInt(minor);
  const absolute = value < 0n ? -value : value;
  return `${value < 0n ? "−" : ""}${(absolute / 100n).toLocaleString("ru-RU")},${String(absolute % 100n).padStart(2, "0")} ${currency}`;
}
const date = (value: string) => new Date(value).toLocaleString("ru-RU");

export function RefundCase({ personId, intentId }: { personId: string; intentId: string }) {
  const [data, setData] = useState<Case | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const load = async () => {
    const current = ++generation.current;
    setPending(true);
    setError("");
    try {
      const next = await request<Case>(`/api/v1/persons/${personId}/wallet/refund-reconciliation/${intentId}`);
      if (current === generation.current) setData(next);
    } catch (failure) {
      if (current === generation.current) setError(failure instanceof ApiError ? failure.message : "Не удалось загрузить платёж для сверки.");
    } finally {
      if (current === generation.current) setPending(false);
    }
  };
  useEffect(() => {
    setData(null);
    void load();
    return () => { generation.current++; };
  }, [personId, intentId]);

  return <section aria-label="Сверка конкретного платежа">
    <h3>Платёж {intentId}</h3>
    <Button variant="ghost" disabled={pending} onClick={() => void load()}>Обновить сведения о платеже</Button>
    {pending && <p role="status">Загрузка платежа для сверки…</p>}
    {error && <p role="alert">{error}{data && " Показанные сведения могут быть устаревшими."}</p>}
    {data && <>
      <p>{data.credit_status === "recorded" ? "Пополнение зачислено: проводка соответствует платежу." : data.credit_status === "not_recorded" ? "Зачисление пополнения не записано; платёж ожидает обработки." : "Требуется проверка учёта: статус платежа и проводка зачисления не согласованы."}</p>
      <p>{data.payment.provider} · ID провайдера: {data.payment.provider_id || "—"} · Статус в хабе: {data.payment.status}</p>
      <p>Сумма платежа: {exactMoney(data.payment.amount_minor, data.payment.currency)}</p>
      <p>Запись чека: {data.payment.receipt_recorded ? "сохранена" : "отсутствует"}. Наличие записи само по себе не подтверждает успешную фискализацию.</p>
      <p>Текущий баланс кошелька: {data.current_balance_minor === null ? "кошелёк не создан" : exactMoney(data.current_balance_minor, "RUB")}</p>
      <p>Общий баланс относится ко всем продуктам клиента и не показывает, какая часть этого пополнения была потрачена.</p>
      {data.credit && <p>Проводка: {data.credit.id} · {date(data.credit.occurred_at)} · {data.credit.kind} · {exactMoney(data.credit.amount_minor, data.payment.currency)} · Баланс после проводки: {exactMoney(data.credit.balance_after_minor, "RUB")} · Ссылка: {data.credit.ref}</p>}
      <h4>Подтверждённые записи возврата этого платежа</h4>
      <p>Отдельные возвраты и снимки общей суммы не складываются.</p>
      <div className="table-scroll"><table>
        <thead><tr><th>Дата</th><th>Источник</th><th>ID провайдера</th><th>Сумма записи</th><th>Общая сумма на момент записи</th></tr></thead>
        <tbody>{data.observations.map(item => <tr key={item.id}>
          <td>{date(item.occurred_at)}</td><td>{item.source_kind === "refund" ? "Отдельный возврат" : item.source_kind === "payment_snapshot" ? "Снимок платежа" : item.source_kind}</td><td>{item.source_id}</td>
          <td>{exactMoney(item.amount_minor, item.currency)}</td><td>{exactMoney(item.cumulative_refunded_minor, item.currency)}</td>
        </tr>)}</tbody>
      </table></div>
      {data.observations_truncated && <p role="status">История ограничена 200 последними записями. Для полного разбора нужна полная история.</p>}
      <p>Статус блокировки: {data.hold.status === "active" ? "требуется ручная сверка" : data.hold.status}. Сверьте данные в ЮKassa и проводки перед принятием решения.</p>
    </>}
  </section>;
}
